/**
 * Supabase client initialization
 * Configured with environment variables for Supabase URL and anonymous key
 */

import { createClient } from "@supabase/supabase-js";
import {
  createSessionExpiryHandler,
  isSessionInvalidError,
} from "./sessionExpiry";

// Get Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate that required environment variables are present
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase environment variables. Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.",
  );
}

/**
 * The localStorage key holding the auth session.
 *
 * Exported because logout has to be able to remove it directly: supabase-js only clears it
 * AFTER its POST /auth/v1/logout resolves, so an aborted or failed request leaves a live
 * token behind. See AuthContext.logout().
 */
export const AUTH_STORAGE_KEY = "uslawshield-crm-auth";

/**
 * THE session-expiry handler (see services/sessionExpiry.ts). One instance per page so that
 * however many requests fail at once there is a single sign-out and a single navigation.
 * `signOut` is resolved lazily because signOutCompletely is declared further down.
 */
export const sessionExpiry = createSessionExpiryHandler({
  signOut: async () => {
    await signOutCompletely();
    // AuthContext's cached profile and session clock. Left behind, the cached profile would
    // render a protected page before any session check on the next visit within 5 minutes.
    try {
      localStorage.removeItem("usls_user_profile");
      localStorage.removeItem("usls_session_start_time");
    } catch {
      // storage blocked
    }
  },
  navigate: (url) => window.location.replace(url),
  getPathname: () => window.location.pathname,
  getSearch: () => window.location.search,
  saveReturnTo: (path) => sessionStorage.setItem("returnTo", path),
  anonAuthorization: `Bearer ${supabaseAnonKey}`,
  recordReason: (source, extra) => {
    localStorage.setItem(
      "usls_last_logout_reason",
      JSON.stringify({ at: new Date().toISOString(), source, ...extra }),
    );
  },
});

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

/**
 * fetch for the persisted client (auth AND data both go through it):
 *  - once session expiry is detected, data requests are refused locally rather than being
 *    sent with the anon key (the 2026-10-03 "permission denied for function has_org_access"
 *    storm). The page is navigating to /login; nothing it fetches now would be shown.
 *  - auth responses that prove the session is dead (GET /auth/v1/user 401/403, refresh grant
 *    400) trigger the handler, whoever made the call — many services call getUser() directly
 *    and ignore its error.
 */
const sessionAwareFetch: typeof fetch = async (input, init) => {
  const url = requestUrl(input);
  const authorization = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  ).get("Authorization");
  if (sessionExpiry.shouldBlockRequest(url, authorization)) {
    return new Response(
      JSON.stringify({
        code: "session_expired",
        message: "Session expired; request not sent.",
        details: null,
        hint: null,
      }),
      { status: 401, headers: { "Content-Type": "application/json" } },
    );
  }
  const response = await fetch(input, init);
  try {
    const method =
      init?.method ??
      (typeof input === "object" && "method" in input ? input.method : "GET");
    sessionExpiry.observeResponse(url, method, response.status);
  } catch {
    // never let detection break a request
  }
  return response;
};

// Initialize Supabase client with auth configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Persist session in local storage
    persistSession: true,
    // Use localStorage instead of IndexedDB to avoid Chrome hanging issues
    storage: window.localStorage,
    // Auto refresh token before expiry
    autoRefreshToken: true,
    // Detect session from URL (for email confirmation, password reset, etc.)
    detectSessionInUrl: true,
    // Storage key for session
    storageKey: AUTH_STORAGE_KEY,
  },
  // Global configuration
  global: {
    headers: {
      "x-application-name": "uslawshield-crm",
    },
    fetch: sessionAwareFetch,
  },
});

// Shared anonymous client for public pages (no auth session persisted)
// Use this instead of creating per-page clients to avoid "Multiple GoTrueClient" warnings
export const anonSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
});

// Helper to get the current session
export const getCurrentSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }
  return data.session;
};

// Helper to get the current user
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    throw error;
  }
  return data.user;
};

/**
 * How long a sign-out waits on the network before giving up and clearing locally.
 *
 * ⚠️ The budget covers auth-js acquiring its storage lock as well as the POST itself, so with
 * several tabs open part of it is spent queueing behind another tab's token refresh. If it
 * expires we skip SERVER-side revocation and fall back to clearing locally — the user is
 * signed out on this device either way, but a shared workstation keeps a live refresh token
 * until it expires. 4s is ~16x the sign-out times measured in production (136-165ms), so that
 * is an accepted edge rather than an unexamined one.
 */
export const SIGN_OUT_NETWORK_TIMEOUT_MS = 4000;

/**
 * THE way to sign out. Every sign-out path goes through here.
 *
 * ⭐ Two guarantees, both learned the hard way on 2026-09-21:
 *
 *  1. It always finishes before it returns, so a caller can clear React state afterwards
 *     without a redirect aborting the in-flight request. `auth-js` only reaches
 *     `_removeSession()` AFTER its POST resolves and bails early on a network error (it
 *     swallows only 401/403/404), so an aborted request used to leave the token in place.
 *  2. The token is removed locally even if the request fails, times out, or 5xx's. A
 *     surviving token is exactly what re-authenticates the user on the next page load —
 *     `/login` sees a valid session and redirects back into the app. An explicit sign-out
 *     must be final even offline.
 *
 * Replaces a previous `signOut` export here and a `logout` in auth.service.ts that both had
 * neither guarantee and no callers.
 *
 * ⚠️ Callers MUST follow this with a navigation. On the timeout path the token is gone from
 * storage but auth-js may still hold the session in memory, and its next auto-refresh would
 * write it straight back.
 */
export async function signOutCompletely(): Promise<void> {
  // Whoever started this sign-out owns the navigation; the SIGNED_OUT it emits must not be
  // mistaken for an unexpected expiry.
  sessionExpiry.noteDeliberateSignOut();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      supabase.auth.signOut(),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, SIGN_OUT_NETWORK_TIMEOUT_MS);
      }),
    ]);
  } catch {
    // The local clear below is the guarantee, not this call.
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }

  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // private mode / blocked storage — nothing more we can do locally
  }
}

// Type for database schema (can be generated from Supabase CLI)
export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: Record<string, unknown>;
        Insert: Record<string, unknown>;
        Update: Record<string, unknown>;
      };
      organizations: {
        Row: Record<string, unknown>;
        Insert: Record<string, unknown>;
        Update: Record<string, unknown>;
      };
      // Add other table types as needed
    };
  };
};

/**
 * Handle auth errors globally — redirect to login ONLY on a definitive
 * "session is invalid" signal from Supabase. NOT on broad message
 * substrings like "session" or "token", which catch transient network
 * errors, PostgREST permission denials, and 5xx responses — that's the
 * cascade that caused the 2026-05-19 mass-logout incident (16 /login?
 * expired redirects across 9 IPs in one morning).
 *
 * Definitive signals we trust:
 *   - error.name === "AuthSessionMissingError"   (supabase-js class)
 *   - error.message === "Auth session missing!"   (exact-string fallback)
 *   - error.code is one of the well-known refresh/session error codes
 *     emitted by GoTrue (refresh_token_not_found, invalid_refresh_token,
 *     refresh_token_already_used, session_not_found, session_expired, bad_jwt)
 *   - error.__isAuthError === true && error.status is 401 or 403
 *     (2026-10-03: a 403 from /auth/v1/user is GoTrue rejecting the JWT — bad_jwt —
 *     and previously left the page running on a dead token with no redirect)
 * The predicate lives in services/sessionExpiry.ts (isSessionInvalidError).
 *
 * Everything else is logged as transient and DOES NOT redirect. A small
 * diagnostic marker is written to localStorage on every call so we can
 * forensically reconstruct what triggered (or did not trigger) a logout.
 */

interface MaybeAuthError {
  name?: string;
  code?: string;
  status?: number;
  message?: string;
  __isAuthError?: boolean;
}

function recordAuthEvent(
  err: MaybeAuthError,
  action: "redirect" | "ignored",
): void {
  try {
    localStorage.setItem(
      "usls_last_auth_event",
      JSON.stringify({
        at: new Date().toISOString(),
        action,
        name: err?.name ?? null,
        code: err?.code ?? null,
        status: err?.status ?? null,
        message: err?.message?.slice(0, 200) ?? null,
      }),
    );
  } catch {
    // Non-critical — localStorage may be full or disabled.
  }
}

export const handleAuthError = (error: unknown): void => {
  const err = (error ?? {}) as MaybeAuthError;

  if (!isSessionInvalidError(err)) {
    // Transient / non-auth / unrecognized — DO NOT redirect.
    // Log so it's visible in dev consoles + leave a diagnostic crumb.
    console.warn(
      "[Supabase] Non-fatal error (not redirecting):",
      err?.name,
      err?.code,
      err?.message,
    );
    recordAuthEvent(err, "ignored");
    return;
  }

  console.error("[Supabase] Definitive session-invalid signal:", err);
  recordAuthEvent(err, "redirect");

  // 2026-10-03: this used to wipe storage by key pattern and assign window.location itself,
  // with its own copy of the public-page list. It now goes through the ONE session-expiry
  // handler: protected pages get a single signOutCompletely() + redirect with the message;
  // auth and public pages are left alone; parallel callers share the same sign-out.
  void sessionExpiry.handle(
    `handleAuthError-${err.code ?? err.name ?? "auth"}`,
  );
};

/**
 * Wrap a Supabase query to handle auth errors automatically
 */
export const withAuthErrorHandling = async <T>(
  queryFn: () => Promise<{ data: T | null; error: Error | null }>,
): Promise<{ data: T | null; error: Error | null }> => {
  const result = await queryFn();
  if (result.error) {
    handleAuthError(result.error);
  }
  return result;
};
